import React, { CSSProperties } from 'react';
import { PostResponseSchema, useGetPostMetadataQuery } from '@/api/client';
import { LivestreamInfoOverlay } from './components/LivestreamInfoOverlay';
import { JoinLivestreamButton } from './components/JoinLivestreamButton';
import UserInfoOverlay from '../components/UserInfoOverlay';
import PostCardBackgroundImage from '../post-item-recorded/components/PostCardBackgroundImage';

interface Props {
  post: PostResponseSchema;
  onVideoClick?: (post: PostResponseSchema) => void;
  wrapperStyle?: React.CSSProperties;
}

export default function PostCardLivestreamPostType({
  post,
  onVideoClick,
}: Props): React.ReactElement {
  const { data: metadata } = useGetPostMetadataQuery(String(post.id));

  const streamStatus = metadata?.[post.id]?.stream_status;
  const viewerCount = metadata?.[post.id]?.current_viewer_count ?? 0;
  const isLive = streamStatus === 'live' && Boolean(post.livestream_id);
  const hasVod = Boolean(post.m3u8_url);
  const isProcessing = streamStatus === 'ended' && !hasVod;

  const handleJoinLivestream = () => {
    if (isLive && post.share_id) {
      window.location.href = `/livestream/${post.share_id}`;
    } else if (hasVod && onVideoClick) {
      onVideoClick(post);
    }
  };

  const handleBackgroundClick = () => {
    if (isLive) {
      handleJoinLivestream();
    } else if (hasVod && onVideoClick) {
      onVideoClick(post);
    }
  };

  return (
    <>
      <div
        style={{
          ...styles.background,
          cursor: isLive || hasVod ? 'pointer' : 'default',
        }}
        onClick={handleBackgroundClick}>
        {post.photo_url_compressed && (
          <>
            <PostCardBackgroundImage post={post} />
            <div style={styles.darkOverlay} />
          </>
        )}

        {isProcessing && (
          <div style={styles.processingOverlay}>
            <span style={styles.processingText}>Processing livestream recording...</span>
            <span style={styles.processingSubtext}>Check back soon</span>
          </div>
        )}
      </div>

      <LivestreamInfoOverlay
        title={post.title}
        streamStatus={streamStatus}
        hasVod={hasVod}
        viewerCount={viewerCount}
        hasImage={!!post.photo_url_compressed}
      />

      <div style={styles.actionsRow}>
        <JoinLivestreamButton onClick={handleJoinLivestream} isLive={isLive} />
      </div>

      <UserInfoOverlay
        profilePic={post.profile_pic}
        username={post.username}
        timestamp={post.timestamp}
        variant="overlay"
      />
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    backgroundColor: '#000000',
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  processingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    pointerEvents: 'none',
  },
  processingText: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  processingSubtext: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '12px',
    fontWeight: '400',
  },
  actionsRow: {
    position: 'absolute',
    bottom: 55,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 10px',
    zIndex: 15,
  },
};
